import merge from 'lodash/merge';
// fetchJSON is bundled wrapper around fetch which simplifies working
// with JSON API:
//   * Automatically adds Content-Type: application/json to request headers
//   * Parses response as JSON when Content-Type: application/json header is
//     present in response headers
//   * Converts non-ok responses to errors
import {configureRefreshFetch, fetchJSON} from 'refresh-fetch';
import customFetch from './customFetch';
import Client from '../../Client';
import LocaleStorage from '../../utils/storage/LocaleStorage';
import {AuthApi} from '../AuthApi';
import {
  getURL,
  ORIGINS,
  LS_I18NEXT_LNG_KEY,
  getAnonymousHeaders,
  getTokenFromLocalStorage,
  persistTokenToLocalStorage,
  removeTokenFromLocalStorage,
} from '../../utils';

const buildRequestHeaders = (initHeaders?: RequestInit['headers']) => {
  const token = getTokenFromLocalStorage();
  const language = LocaleStorage.get<string>(LS_I18NEXT_LNG_KEY);
  const languageCode = language?.slice(0, 2);
  const storedApiKey = Client.initParams?.apikey;
  const isChekinConsumer = Client.initParams?.isChekinConsumer;
  let headers = initHeaders || {'Content-Type': 'application/json'};

  if (!isChekinConsumer) {
    headers = merge({}, headers, {
      'embedded-sdk-app': storedApiKey,
    });
  }

  if (token !== null) {
    headers = merge({}, headers, {
      Authorization: `JWT ${token}`,
      ...(languageCode ? {'Accept-Language': languageCode} : undefined),
      'X-source': isChekinConsumer ? ORIGINS.guestAppV2 : ORIGINS.embeddedGuestApp,
    });
  }

  return headers;
};

// Add token to the request headers
const fetchJSONWithToken = (url: string, initOptions: RequestInit = {}) => {
  const builtHeaders = buildRequestHeaders(initOptions?.headers);
  const options = {...initOptions, headers: builtHeaders};

  return customFetch(url, options);
};

// Decide whether this error returned from API means that we want
// to try refreshing the token. error.response contains the fetch Response
// object, error.body contains the parsed JSON response body
const shouldRefreshToken = (error: {response: Response}) => {
  return error.response && error.response.status === 401;
};

const refreshToken = async () => {
  const storedApiKey = Client.initParams?.apikey;

  try {
    const body = JSON.stringify({apikey: storedApiKey});

    const result = await fetchJSON<{access_token: string}>(
      getURL(AuthApi.ENDPOINTS.getToken(), 'v4'),
      {
        method: 'POST',
        headers: getAnonymousHeaders(),
        body,
      },
    );

    const token = result.body?.access_token;
    persistTokenToLocalStorage(token);
  } catch (err) {
    removeTokenFromLocalStorage();
  }
};

const refreshFetch = configureRefreshFetch({
  shouldRefreshToken,
  refreshToken,
  fetch: fetchJSONWithToken,
});

export {refreshFetch};
